import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, MessageCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "919000000000";

const GOALS = ["Fat loss", "Muscle / physique", "Strength / powerlifting", "Recomposition", "Performance", "Lifestyle transformation", "Other"];
const COMMITMENT_LABELS = ["Just exploring", "Interested", "Serious", "Very serious", "I'm ready to make a change"];
const INVESTMENT_OPTIONS = ["Yes, I'm prepared to invest", "Possibly, depending on the investment", "No"];

const inputCls = "w-full bg-[#0d0d10] border border-white/10 focus:border-[#ccff00] outline-none px-4 py-3.5 text-base text-white placeholder:text-zinc-600 transition-colors";
const labelCls = "block font-mono-alt text-[11px] tracking-[0.25em] uppercase text-zinc-500 mb-2.5";

const initial = {
  name: "", age: "", whatsapp: "", instagram: "",
  goal: "", struggle: "", desired_outcome: "", tried_before: "",
  commitment: 0, investment: "",
};

export default function Apply() {
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (step === 0) {
      if (!form.name.trim()) return "Please enter your name.";
      if (!form.age || Number(form.age) < 10 || Number(form.age) > 100) return "Please enter a valid age.";
      if (!form.whatsapp.trim() || form.whatsapp.replace(/\D/g, "").length < 8) return "Please enter your WhatsApp number with country code.";
    }
    if (step === 1 && !form.goal) return "Please pick your primary goal.";
    if (step === 2) {
      if (!form.struggle.trim()) return "Tell me what you're struggling with.";
      if (!form.desired_outcome.trim()) return "Tell me what success looks like for you.";
      if (!form.tried_before.trim()) return "Tell me what you've tried before.";
    }
    if (step === 3) {
      if (!form.commitment) return "Pick how serious you are (1–5).";
      if (!form.investment) return "Please answer the investment question.";
    }
    return null;
  };

  const next = () => {
    const err = validate();
    if (err) return toast.error(err);
    setDir(1);
    setStep((s) => s + 1);
  };

  const back = () => { setDir(-1); setStep((s) => s - 1); };

  const submit = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setSubmitting(true);
    const payload = { ...form, age: Number(form.age) };
    const msg = [
      "Hey Nimish, I just applied for coaching.",
      `Name: ${form.name} (${form.age})`,
      `Goal: ${form.goal}`,
      `Biggest struggle: ${form.struggle}`,
      `6–12 month outcome: ${form.desired_outcome}`,
      `Tried before: ${form.tried_before}`,
      `Commitment: ${form.commitment}/5 (${COMMITMENT_LABELS[form.commitment - 1]})`,
      `Investment ready: ${form.investment}`,
    ].join("\n");
    const waUrl = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
    // Open synchronously inside the click handler so browsers don't block the popup
    const waWindow = window.open(waUrl, "_blank");
    try {
      await axios.post(`${API}/applications`, payload);
    } catch (e) {
      toast.error("Couldn't save your application, but WhatsApp will still work.");
    }
    if (!waWindow) toast.error("Your browser blocked the WhatsApp popup. Tap the button below to continue.");
    setDone(waUrl);
    setSubmitting(false);
  };

  const steps = ["About you", "Your goal", "Your story", "Commitment"];

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-[#f4f4f6]" data-testid="apply-page">
      <nav className="border-b border-white/10 backdrop-blur-xl bg-black/70">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" data-testid="apply-nav-logo" className="block py-2">
            <img src="/logo.png" alt="Strength Syndicate" className="h-16 w-auto" />
          </Link>
          <span className="font-mono-alt text-[11px] tracking-[0.25em] uppercase text-zinc-500">Coaching Application</span>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        {done ? (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-center py-16" data-testid="apply-success">
            <span className="inline-flex w-16 h-16 items-center justify-center bg-[#ccff00]/15 text-[#ccff00] mb-8">
              <Check size={30} />
            </span>
            <h1 className="font-display uppercase text-4xl sm:text-5xl leading-tight">Application received.</h1>
            <p className="mt-6 text-zinc-300 max-w-md mx-auto leading-relaxed">
              Your application is in. The fastest way to lock in your spot is to continue on WhatsApp.
              Your answers are already filled in.
            </p>
            <a
              href={done}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="success-whatsapp-btn"
              className="mt-10 inline-flex items-center gap-3 bg-[#25d366] hover:bg-[#1ebe57] text-black font-mono-alt text-xs tracking-[0.2em] uppercase px-8 py-4 transition-colors"
            >
              <MessageCircle size={16} />
              Continue on WhatsApp
            </a>
            <div className="mt-6">
              <Link to="/" className="font-mono-alt text-[11px] tracking-[0.25em] uppercase text-zinc-500 hover:text-white transition-colors" data-testid="success-home-link">
                Back to site
              </Link>
            </div>
          </motion.div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-12" data-testid="apply-progress">
              {steps.map((s, i) => (
                <div key={s} className="flex-1">
                  <div className={`h-1 transition-colors duration-500 ${i <= step ? "bg-[#ccff00]" : "bg-white/10"}`} />
                  <p className={`mt-2 font-mono-alt text-[10px] tracking-[0.2em] uppercase ${i === step ? "text-white" : "text-zinc-600"}`}>
                    {`0${i + 1}. ${s}`}
                  </p>
                </div>
              ))}
            </div>

            <AnimatePresence mode="wait" custom={dir}>
              <motion.div
                key={step}
                custom={dir}
                initial={{ opacity: 0, x: 40 * dir }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 * dir }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              >
                {step === 0 && (
                  <div data-testid="apply-step-basics">
                    <h1 className="font-display uppercase text-4xl sm:text-5xl leading-tight mb-10">Let's start with the basics.</h1>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className={labelCls}>Full name *</label>
                        <input data-testid="input-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" />
                      </div>
                      <div>
                        <label className={labelCls}>Age *</label>
                        <input data-testid="input-age" type="number" className={inputCls} value={form.age} onChange={(e) => set("age", e.target.value)} placeholder="27" />
                      </div>
                      <div>
                        <label className={labelCls}>WhatsApp number (with country code) *</label>
                        <input data-testid="input-whatsapp" className={inputCls} value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+91 98765 43210" />
                      </div>
                      <div>
                        <label className={labelCls}>Instagram username</label>
                        <input data-testid="input-instagram" className={inputCls} value={form.instagram} onChange={(e) => set("instagram", e.target.value)} placeholder="@yourhandle" />
                      </div>
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div data-testid="apply-step-goal">
                    <h1 className="font-display uppercase text-4xl sm:text-5xl leading-tight mb-4">What's your primary goal right now?</h1>
                    <p className="text-zinc-400 mb-10">Pick the one that matters most.</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {GOALS.map((g) => (
                        <button
                          key={g}
                          type="button"
                          data-testid={`goal-option-${g.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          onClick={() => set("goal", g)}
                          className={`text-left px-5 py-4 border font-medium transition-all duration-200 ${
                            form.goal === g
                              ? "border-[#ccff00] bg-[#ccff00]/10 text-white"
                              : "border-white/10 bg-[#0d0d10] text-zinc-400 hover:border-white/30 hover:text-white"
                          }`}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div data-testid="apply-step-story">
                    <h1 className="font-display uppercase text-4xl sm:text-5xl leading-tight mb-10">Your story, honestly.</h1>
                    <div className="space-y-8">
                      <div>
                        <label className={labelCls}>What are you currently struggling with most? *</label>
                        <textarea data-testid="input-struggle" rows={3} className={inputCls} value={form.struggle} onChange={(e) => set("struggle", e.target.value)} placeholder="e.g. I train consistently but haven't seen visible progress in a year…" />
                      </div>
                      <div>
                        <label className={labelCls}>If we spoke 6–12 months from now and coaching went perfectly, what would be different? *</label>
                        <textarea data-testid="input-outcome" rows={3} className={inputCls} value={form.desired_outcome} onChange={(e) => set("desired_outcome", e.target.value)} placeholder="Describe the outcome you actually want…" />
                      </div>
                      <div>
                        <label className={labelCls}>What have you tried before? *</label>
                        <textarea data-testid="input-tried" rows={3} className={inputCls} value={form.tried_before} onChange={(e) => set("tried_before", e.target.value)} placeholder="Programs, diets, coaches, apps…" />
                      </div>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div data-testid="apply-step-commitment">
                    <h1 className="font-display uppercase text-4xl sm:text-5xl leading-tight mb-10">Last part. How serious are you?</h1>
                    <label className={labelCls}>Commitment level *</label>
                    <div className="grid grid-cols-5 gap-2 mb-3" data-testid="commitment-scale">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          type="button"
                          data-testid={`commitment-${n}`}
                          onClick={() => set("commitment", n)}
                          className={`py-4 border font-display text-xl transition-all duration-200 ${
                            form.commitment === n
                              ? "border-[#ccff00] bg-[#ccff00] text-black"
                              : "border-white/10 bg-[#0d0d10] text-zinc-400 hover:border-white/30"
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                    <p className="text-sm text-zinc-500 mb-10 h-5">
                      {form.commitment ? COMMITMENT_LABELS[form.commitment - 1] : "1 = just exploring · 5 = ready to make a change"}
                    </p>
                    <label className={labelCls}>Are you financially prepared to invest in premium 1:1 coaching if we're a good fit? *</label>
                    <div className="space-y-3" data-testid="investment-options">
                      {INVESTMENT_OPTIONS.map((opt, i) => (
                        <button
                          key={opt}
                          type="button"
                          data-testid={`investment-option-${i}`}
                          onClick={() => set("investment", opt)}
                          className={`w-full text-left px-5 py-4 border font-medium transition-all duration-200 ${
                            form.investment === opt
                              ? "border-[#ccff00] bg-[#ccff00]/10 text-white"
                              : "border-white/10 bg-[#0d0d10] text-zinc-400 hover:border-white/30 hover:text-white"
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="mt-12 flex items-center justify-between border-t border-white/10 pt-8">
              {step > 0 ? (
                <button onClick={back} data-testid="apply-back-btn" className="flex items-center gap-2 font-mono-alt text-[11px] tracking-[0.25em] uppercase text-zinc-500 hover:text-white transition-colors">
                  <ArrowLeft size={14} /> Back
                </button>
              ) : <span />}
              {step < 3 ? (
                <button onClick={next} data-testid="apply-next-btn" className="group flex items-center gap-3 bg-[#ccff00] hover:bg-[#a8d600] text-black font-mono-alt text-xs tracking-[0.2em] uppercase px-8 py-4 transition-colors">
                  Continue <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={submitting}
                  data-testid="apply-submit-btn"
                  className="group flex items-center gap-3 bg-[#25d366] hover:bg-[#1ebe57] disabled:opacity-50 text-black font-mono-alt text-xs tracking-[0.2em] uppercase px-8 py-4 transition-colors"
                >
                  {submitting ? <Loader2 size={15} className="animate-spin" /> : <MessageCircle size={15} />}
                  Submit & open WhatsApp
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
